import { usePostHog } from "posthog-js/react";
import { useState } from "react";

import type { PollDto } from "@/api/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { formatDate, FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { EyeOffIcon } from "@/components/Icons/EyeOffIcon";
import { MarkerPin01Icon } from "@/components/Icons/MarkerPin01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { LastActivityLabel } from "@/components/Label/LastActivityLabel";
import { ShareButton } from "@/components/ShareButton/ShareButton";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { CommunityContentRail } from "@/product-patterns/community/CommunityContentRail";
import { pollMutations } from "@/queries/polls/mutations";
import { useTranslation } from "@/translations";

import { Announcement02Icon } from "../Icons/Announcement02Icon";
import { ClockIcon } from "../Icons/ClockIcon";
import { Edit05Icon } from "../Icons/Edit05Icon";
import { CodeOfConductDeletionModal } from "./CodeOfConductDeletionModal";
import { CommunityMessageActivitySection } from "./CommunityMessageActivitySection";
import { CommunityPollContent } from "./CommunityPollContent";
import { CommunityPollEditForm } from "./CommunityPollEditForm";
import { CommunityPollResultModal } from "./CommunityPollResultModal";
import { CommunityPost } from "./CommunityPost";
import { CommunityPostReachDetailsModal } from "./CommunityPostReachDetailsModal";
import {
  getCommunityPollStatus,
  MIN_COMMUNITY_POST_REACH_DETAILS_DATE,
  useTrackCommunityPostViewCount,
} from "./helpers";
import type { CommunityMessageActivity } from "./types";
import { CommunityPostStatus } from "./types";

interface CommunityPollProps {
  poll: PollDto;
  /** Opens the discussion on initial render. */
  isExpanded?: boolean;
}
export function CommunityPoll({ poll, isExpanded }: CommunityPollProps): React.ReactNode {
  const [hasBeenInViewport, setHasBeenInViewport] = useState(false);
  const [isDeleteModalOpened, deleteModalHandler] = useBool(false);
  const [isAppPollModalOpened, appPollModalHandler] = useBool(false);
  const [isResultModalOpened, resultModalHandler] = useBool(false);
  const [isEditing, editHandler] = useBool(false);
  const [isReachDetailsModalOpened, reachDetailsModalHandler] = useBool(false);
  const [isTranslated, translationHandler] = useBool(() => !!poll.autoTranslation);

  const { onStartTracking, onCancelTracking } = useTrackCommunityPostViewCount({
    itemId: poll.id,
    type: "poll",
    isEnabled: !poll.viewTracked,
  });

  const postHog = usePostHog();
  const { t, i18n } = useTranslation();
  const sessionUser = useSessionUser();

  const updatePollReadStatusMutation = pollMutations.useUpdatePollReadStatus();
  const deletePollMutation = pollMutations.useDeletePoll();

  const handleEnterViewport = (): void => {
    setHasBeenInViewport(true);
    onStartTracking();
  };

  const handleLeaveViewport = (): void => {
    if (!updatePollReadStatusMutation.isPending && poll.isUnread) {
      updatePollReadStatusMutation.mutate(poll.id);
    }
    onCancelTracking();
  };

  const handleToggleTranslation = () => {
    const hasAutoTranslation = !!poll.autoTranslation;

    if (hasAutoTranslation) {
      postHog.capture(
        isTranslated
          ? "community_auto_translation_show_original_clicked"
          : "community_auto_translation_show_translation_clicked",
        { pollId: poll.id },
      );
    } else if (!isTranslated) {
      postHog.capture("community_manual_translate_clicked", { pollId: poll.id });
    }

    translationHandler.toggle();
  };

  const handleViewReachDetails = () => {
    reachDetailsModalHandler.setTrue();

    postHog.capture("clicked_view_reach_details", {
      pollId: poll.id,
      isOwnMessage: poll.createdBy.id === sessionUser.id,
    });
  };

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("component.community-post.actions.mark-as-read"),
      },
      icon: <CheckIcon />,
      value: "mark-read",
      type: "button",
      callbackFn: () => updatePollReadStatusMutation.mutate(poll.id),
      isHidden: !poll.isUnread,
    },
    {
      labels: {
        default: t("common.action.edit"),
      },
      value: "edit",
      icon: <Edit05Icon />,
      "data-testid": "context-menu-edit-btn",
      callbackFn: editHandler.setTrue,
      isHidden: !poll.canEdit,
    },
    {
      labels: {
        default: t("component.community-poll.actions.delete"),
      },
      icon: <Trash02Icon />,
      value: "delete",
      type: "button",
      "data-testid": "context-menu-delete-btn",
      callbackFn: deleteModalHandler.toggle,
      isHidden: !poll.canDeleteOwn && !poll.canDeleteOther,
    },
  ];
  const canBeShared = !poll.deletedAt;
  const isAnnouncement = poll.type === "announcement" || poll.type === "announcementReadOnly";
  const status = getCommunityPollStatus(poll);
  const socialMessage: CommunityMessageActivity | undefined = poll.messageId
    ? {
        id: poll.messageId,
        type: poll.type,
        canComment: poll.canComment,
        canLike: poll.canLike,
        canShare: poll.canShare,
        requesterReactionType: poll.requesterReactionType,
        totalCommentCount: poll.totalCommentCount,
        totalReactionCountByType: poll.totalReactionCountByType,
        postedAt: poll.postedAt,
        scheduledAt: poll.scheduledAt,
        deletedAt: poll.deletedAt,
        archivedAt: undefined,
        lastActivityAt: poll.lastActivityAt,
        isInactivePost: false,
        group: poll.group,
      }
    : undefined;
  const isReachDetailsAvailable =
    (!!poll.postedAt || !!poll.scheduledAt) && poll.createdBy.isAdmin
      ? new Date(poll.postedAt || poll.scheduledAt!) >= MIN_COMMUNITY_POST_REACH_DETAILS_DATE
      : false;

  return (
    <>
      <CommunityPost.Root
        isUnread={poll.isUnread}
        isArchivedOrDeleted={Boolean(poll.deletedAt)}
        user={poll.createdBy}
        group={poll.group}
        actions={menuActions}
        dateSubtitle={poll.createdAt && <FormattedDate format="datetime" date={poll.createdAt} />}
        renderHeaders={() => (
          <>
            {status === CommunityPostStatus.SCHEDULED && (
              <CommunityPost.Header
                variant="warning"
                title={t("component.community-post.header.title.scheduled", {
                  dateTime: formatDate(i18n, "datetime", poll.scheduledAt!),
                })}
                data-testid="post-scheduled-date"
                icon={<ClockIcon />}
                onClick={isReachDetailsAvailable ? handleViewReachDetails : undefined}
              />
            )}
            {/* For super admins */}
            {status === CommunityPostStatus.DELETED && (
              <CommunityPost.Header
                variant="danger"
                title={t("component.community-post.header.title.deleted")}
                icon={<Trash02Icon />}
                onClick={isReachDetailsAvailable ? handleViewReachDetails : undefined}
              />
            )}
            {/* For admins */}
            {status === CommunityPostStatus.DELIVERED && sessionUser.isAdmin && isAnnouncement && (
              <CommunityPost.Header
                variant="success"
                title={t("component.community-post.header.title.delivered")}
                icon={<CheckIcon />}
                onClick={isReachDetailsAvailable ? handleViewReachDetails : undefined}
              />
            )}
            {/* For residents */}
            {poll.isHiddenFromAdmins && (
              <CommunityPost.Header title={t("component.community-post.header.title.internal")} icon={<EyeOffIcon />} />
            )}
          </>
        )}
        renderBadges={() => (
          <>
            {poll.projectConnection && (
              <Badge variant="info" icon={<MarkerPin01Icon />}>
                {poll.projectConnection?.name}
              </Badge>
            )}
            {isAnnouncement && (
              <Badge data-testid="announcement-label" variant="special" icon={<Announcement02Icon />}>
                {t("component.community-post.label.announcement")}
              </Badge>
            )}
          </>
        )}
        onEnterViewport={handleEnterViewport}
        onLeaveViewport={handleLeaveViewport}
      >
        {(!poll.deletedAt || sessionUser.isAdmin) && (
          <Stack gap="md">
            {isEditing ? (
              <CommunityContentRail>
                <CommunityPollEditForm poll={poll} onEdit={editHandler.setFalse} onCancel={editHandler.setFalse} />
              </CommunityContentRail>
            ) : (
              <CommunityPollContent
                poll={poll}
                shouldLoad={hasBeenInViewport}
                onViewResults={resultModalHandler.setTrue}
                isTranslated={isTranslated}
                onToggleTranslation={handleToggleTranslation}
              />
            )}
            {!poll.scheduledAt && socialMessage && (
              <CommunityMessageActivitySection message={socialMessage} pollId={poll.id} isExpanded={isExpanded} />
            )}
          </Stack>
        )}
        {(!socialMessage || poll.scheduledAt) && (poll.lastActivityAt || canBeShared) && (
          <aside className="ml-auto flex items-center gap-space-md">
            {poll.lastActivityAt && <LastActivityLabel lastActivityAt={poll.lastActivityAt} />}
            {canBeShared && <ShareButton entityId={poll.id} entityType="poll" variant="ghostPrimary" />}
          </aside>
        )}
      </CommunityPost.Root>

      <CommunityPollResultModal
        isOpened={isResultModalOpened}
        onOpenChange={resultModalHandler.set}
        poll={poll}
        isTranslated={isTranslated}
        onToggleTranslation={handleToggleTranslation}
      />
      {poll.canDeleteOwn ? (
        <ConfirmModal
          title={t("component.community-poll.delete.modal.title")}
          description={t("component.community-poll.delete.modal.description")}
          isLoading={deletePollMutation.isPending}
          tone="error"
          onReject={deleteModalHandler.setFalse}
          onOpenChange={deleteModalHandler.set}
          rejectBtnProps={{
            "data-testid": "delete-modal-cancel",
          }}
          onResolve={() => {
            deletePollMutation.mutate(poll.id, { onSuccess: deleteModalHandler.setFalse });
          }}
          resolveBtnProps={{
            "data-testid": "modal-confirm-delete",
            text: t("common.action.delete"),
          }}
          isOpened={isDeleteModalOpened}
          shouldCloseOnEsc
          data-testid="cancel-edit-modal"
        />
      ) : (
        <CodeOfConductDeletionModal
          isLoading={deletePollMutation.isPending}
          isOpened={isDeleteModalOpened}
          onOpenChange={deleteModalHandler.set}
          onDelete={() => {
            deletePollMutation.mutate(poll.id, { onSuccess: deleteModalHandler.setFalse });
          }}
        />
      )}
      <ConfirmModal
        title={t("component.community-post.app-poll.modal.title")}
        description={t("component.community-post.app-poll.modal.description")}
        renderDescription={(description) => (
          <Stack align="center">
            <span>{description}</span>
            <Button type="link" variant="ghostPrimary" href={t("component.community-post.app-poll.modal.link")}>
              {t("component.community-post.app-poll.modal.link")}
            </Button>
          </Stack>
        )}
        isLoading={false}
        tone="primary"
        onResolve={appPollModalHandler.setFalse}
        onReject={appPollModalHandler.setFalse}
        onOpenChange={appPollModalHandler.set}
        rejectBtnProps={{
          hide: true,
        }}
        resolveBtnProps={{
          "data-testid": "app-poll-modal-confirm",
          text: t("common.action.ok"),
        }}
        isOpened={isAppPollModalOpened}
        shouldCloseOnEsc
        data-testid="cancel-app-poll-modal"
      />
      {status === CommunityPostStatus.SCHEDULED && (
        <CommunityPostReachDetailsModal
          isOpened={isReachDetailsModalOpened}
          onOpenChange={reachDetailsModalHandler.set}
          postId={poll.id}
          onEditMessage={editHandler.setTrue}
          status={status}
          postType="poll"
        />
      )}
      {(status === CommunityPostStatus.DELIVERED ||
        status === CommunityPostStatus.DELETED ||
        status === CommunityPostStatus.ARCHIVED) && (
        <CommunityPostReachDetailsModal
          isOpened={isReachDetailsModalOpened}
          onOpenChange={reachDetailsModalHandler.set}
          postId={poll.id}
          status={status}
          postType="poll"
        />
      )}
    </>
  );
}
